/**
 * 资讯频道（news-topbar / news-feed 的 channels 属性）双向转换。
 *
 * 存储形态与客户端（mxjl）解析契约对齐：
 * - 兼容旧版逗号/中文逗号分隔字符串（首次加载拆成列表项，空段丢弃）；
 * - 编辑器提交统一写为 JSON 数组：普通频道为字符串名，action 频道为
 *   `{ "name": "...", "action": "/route" }`（点击直接跳转路由，如彭城微短剧 /market）。
 *
 * 客户端会跳过空名条目；编辑器为保持行内编辑稳定，数组形态下的空名行保留回显，
 * 避免用户在清空名称输入框的瞬间整行被吞掉。
 */
export interface NewsChannelItem {
  name: string
  action: string
}

/** channels 可视化列表编辑器适用的组件类型。 */
const NEWS_CHANNELS_COMPONENT_TYPES = new Set(['news-topbar', 'news-feed'])

export function isNewsChannelsField(componentType: string, fieldCode: string): boolean {
  return fieldCode === 'channels' && NEWS_CHANNELS_COMPONENT_TYPES.has(componentType)
}

/**
 * 把存储的 channels 值解析为编辑器列表项。
 * 字符串形态按逗号拆分并丢弃空段；数组形态逐项规整，空名行保留（见模块注释）。
 */
export function parseNewsChannels(value: unknown): NewsChannelItem[] {
  if (Array.isArray(value)) {
    return value.flatMap((entry) => {
      const item = normalizeChannelEntry(entry)
      return item ? [item] : []
    })
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value)
      .split(/[,，]/)
      .flatMap((segment) => {
        const name = normalizeChannelText(segment)
        return name ? [{ name, action: '' }] : []
      })
  }
  return []
}

/**
 * 把编辑器列表项序列化为存储值：普通频道写字符串名，action 频道写
 * `{ name, action }` 对象；空名且无路由的行写为空字符串（客户端跳过）。
 */
export function serializeNewsChannels(items: readonly NewsChannelItem[]): Array<string | { name: string; action: string }> {
  return items.map((item) => {
    const name = normalizeChannelText(item?.name)
    const action = normalizeChannelText(item?.action)
    return action ? { name, action } : name
  })
}

/** 预览/展示用的频道名列表：提取所有形态的名称并丢弃空名。 */
export function newsChannelNames(value: unknown): string[] {
  return parseNewsChannels(value)
    .map((item) => item.name)
    .filter(Boolean)
}

function normalizeChannelEntry(entry: unknown): NewsChannelItem | null {
  if (typeof entry === 'string' || typeof entry === 'number') {
    return { name: normalizeChannelText(entry), action: '' }
  }
  if (entry && typeof entry === 'object') {
    const record = entry as Record<string, unknown>
    const name = normalizeChannelText(record.name ?? record.title ?? record.label ?? record.category)
    const action = normalizeChannelText(record.action ?? record.actionRoute ?? record.route)
    // 完全空的对象没有可编辑内容，直接丢弃；空名但带路由的行保留，避免行内编辑中途丢行。
    if (!name && !action) return null
    return { name, action }
  }
  return null
}

function normalizeChannelText(value: unknown): string {
  if (typeof value !== 'string' && typeof value !== 'number') return ''
  return String(value).replace(/\s+/g, ' ').trim()
}
